import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import CollapseHeader from '../../../../core/common/collapse-header';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import ViewServiceModal from './ViewServiceModal';
import CompleteModule from './CompleteModule';
import ApprovedCompletedModule from './ApprovedCompletedModule';
import RejectedCompletedModule from './RejectedCompletedModule';
import PendingCompletedModule from './PendingCompletedModule';
import PendingApprovedModule from './PendingApprovedModule';
import apidefinitions from '../../../../core/api/apiDefinitions';
import { Button, Table } from 'react-bootstrap';
import moment from 'moment';
import Swal from 'sweetalert2';
import { all_routes } from '../../../router/all_routes'; // Adjust path to your routes file

export interface ServiceData {
  id: number;
  vehicleId: string;
  serviceType: string;
  supervisorId: string | null;
  serviceDate: string | null;
  serviceKms: number;
  serviceCost: number;
  serviceDescription: string | null;
  serviceStatus: string;
  approvalStatus: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  billPhotoUrls: string[] | null;
  servicedVehicleParts: {
    vehiclePartId: number;
    name: string;
    comment: string;
    price: number;
    partSource: string | null;
    partYear: string | null;
    partCondition: string | null;
    serialNo: string;
    warrantyPeriod: number;
  }[];
  createdBy: string | null;
  createdAt: string;
}

interface ServiceCounts {
  approved: number;
  rejected: number;
  pending: number;
  pendingApproved: number;
}

interface User {
  empNumber: string;
  name: string;
  createdAt: string;
  status: string;
  email?: string;
  phone?: string;
  userType?: string;
  departmentNames?: string[];
}

const statusPermissions: { [key: string]: string } = {
  APPROVED: 'ROLE_SERVICE',
  REJECTED: 'ROLE_SERVICE',
  PENDING: 'SERVICE_MANAGER',
  PENDING_APPROVED: 'ROLE_FINANCE_APPROVE_SERVICE',
};

const statusColors: { [key: string]: string } = {
  APPROVED: 'badge bg-success',
  REJECTED: 'badge bg-primary',
  PENDING: 'badge bg-secondary',
  PENDING_APPROVAL: 'badge bg-info',
  COMPLETED: 'badge bg-success',
};

const statusCardStyles: {
  [key: string]: { bgColor: string; iconColor: string; icon: string; className: string };
} = {
  APPROVED: { bgColor: '#d1fae5', iconColor: '#10b981', icon: 'fas fa-check-circle', className: 'bg-green-100' },
  REJECTED: { bgColor: '#fee2e2', iconColor: '#ef4444', icon: 'fas fa-times-circle', className: 'bg-red-100' },
  PENDING: { bgColor: '#fef3c7', iconColor: '#f59e0b', icon: 'fas fa-clock', className: 'bg-amber-100' },
  PENDING_APPROVED: { bgColor: '#e0f2fe', iconColor: '#3b82f6', icon: 'fas fa-check-double', className: 'bg-blue-100' },
};

const statusLabels: { [key: string]: string } = {
  APPROVED: 'Approved Completed',
  REJECTED: 'Rejected Completed',
  PENDING: 'Pending Completed',
  PENDING_APPROVED: 'Pending Approved',
};

type CardStatus = 'PENDING' | 'PENDING_APPROVED' | 'APPROVED' | 'REJECTED';

// Navigation items for the Service section
const navItems = [
  { label: 'Service Dashboard', icon: 'ti ti-car', link: all_routes.service, permission: 'SERVICE_MANAGER' },
  { label: 'Add Service', icon: 'ti ti-plus', link: all_routes.addservice, permission: 'SERVICE_MANAGER' },
];

const buildPayloadForCard = (status: CardStatus) => {
  if (status === 'PENDING') {
    return { filterType: 'SERVICE_STATUS', filterValue: 'PENDING', date: '' };
  }
  if (status === 'PENDING_APPROVED') {
    return { filterType: 'APPROVAL_STATUS', filterValue: 'PENDING_APPROVAL', date: '' };
  }
  return { filterType: 'APPROVAL_STATUS', filterValue: status, date: '' };
};

const Service: React.FC = () => {
  const locationHook = useLocation();
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showApprovedModal, setShowApprovedModal] = useState(false);
  const [showRejectedModal, setShowRejectedModal] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [showPendingApprovedModal, setShowPendingApprovedModal] = useState(false);

  const [services, setServices] = useState<ServiceData[]>([]);
  const [selectedService, setSelectedService] = useState<ServiceData | null>(null);
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageNo, setPageNo] = useState(1);
  const [total, setTotal] = useState(0);

  const [counts, setCounts] = useState<ServiceCounts>({
    approved: 0,
    rejected: 0,
    pending: 0,
    pendingApproved: 0,
  });

  const [initialPartName, setInitialPartName] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<CardStatus | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const storedUsername = localStorage.getItem('username');
      if (!storedUsername) {
        return;
      }

      const res = await apidefinitions.getUserByEmpNumber(storedUsername);
      if (res.data.status === 'S1000' && res.data.data) {
        const data = res.data.data;
        const user: User = {
          empNumber: data.empNumber?.toString() || '',
          name: data.name || 'Unknown',
          createdAt: data.createdAt
            ? new Date(data.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })
            : 'N/A',
          status: data.status || 'PENDING',
          email: data.email || '',
          phone: data.phone || '',
          userType: data.group || 'USER',
          departmentNames: data.departmentAdminSpecificData?.departmentNames || [],
        };
        setCurrentUser(user);
      } else {
      }
    } catch (err) {
    }
  }, []);

  const fetchCounts = useCallback(async () => {
    try {
      const [approvedRes, rejectedRes, pendingCompletedRes, pendingApprovedRes] = await Promise.all([
        apidefinitions.getVehiclesServiceData(1, { filterType: 'APPROVAL_STATUS', filterValue: 'APPROVED', date: '' }),
        apidefinitions.getVehiclesServiceData(1, { filterType: 'APPROVAL_STATUS', filterValue: 'REJECTED', date: '' }),
        apidefinitions.getVehiclesServiceData(1, { filterType: 'SERVICE_STATUS', filterValue: 'PENDING', date: '' }),
        apidefinitions.getVehiclesServiceData(1, { filterType: 'APPROVAL_STATUS', filterValue: 'PENDING_APPROVAL', date: '' }),
      ]);

      setCounts({
        approved: approvedRes?.data?.data?.total || 0,
        rejected: rejectedRes?.data?.data?.total || 0,
        pending: pendingCompletedRes?.data?.data?.total || 0,
        pendingApproved: pendingApprovedRes?.data?.data?.total || 0,
      });
    } catch (err) {
    }
  }, []);

  const fetchServices = useCallback(
    async (page: number, card: CardStatus | null) => {
      setLoading(true);
      setError(null);
      try {
        const payload = card ? buildPayloadForCard(card) : { filterType: '', filterValue: '', date: '' };
        const res = await apidefinitions.getVehiclesALLServiceData(page, payload);
        const { vehiclesServiceData, total } = res.data.data;
        setServices(vehiclesServiceData || []);
        setTotal(total || 0);
      } catch (err) {
        setError('Failed to fetch service data. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchCurrentUser();
    fetchServices(pageNo, statusFilter);
    fetchCounts();
  }, [pageNo, statusFilter, fetchServices, fetchCounts, fetchCurrentUser]);

  const handleView = (service: ServiceData) => {
    const partName = service.servicedVehicleParts.length > 0 ? service.servicedVehicleParts[0].name : 'Unknown Part';
    setInitialPartName(partName);
    setSelectedService(service);
    setSelectedServiceId(service.id);
    setShowViewModal(true);
  };

  const handleComplete = (service: ServiceData) => {
    setSelectedService(service);
    setShowCompleteModal(true);
  };

  const handleCardClick = async (status: CardStatus) => {
    setLoading(true);
    setError(null);
    try {
      setStatusFilter(status);
      setPageNo(1);
      await fetchServices(1, status);

      if (status === 'APPROVED') setShowApprovedModal(true);
      else if (status === 'REJECTED') setShowRejectedModal(true);
      else if (status === 'PENDING') setShowPendingModal(true);
      else if (status === 'PENDING_APPROVED') setShowPendingApprovedModal(true);
    } catch (err) {
      setError(`Failed to fetch ${status.toLowerCase()} services. Please try again.`);
      Swal.fire({
        icon: 'error',
        title: 'Fetch Failed',
        text: `Failed to fetch ${status.toLowerCase()} services.`,
        confirmButtonText: 'OK',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= Math.ceil(total / 10)) {
      setPageNo(newPage);
    }
  };

  const handleCompleteSuccess = async () => {
    await fetchServices(pageNo, statusFilter);
    await fetchCounts();
  };

  const isFinanceDepartmentHead = currentUser?.departmentNames?.includes('Finance Division');

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Vehicle Management/Service/Service Dashboard</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="card">
              <div className="card-body pb-0 pt-2">
                <ul className="nav nav-tabs nav-tabs-bottom" style={{ gap: '12px', display: 'flex' }}>
                  {navItems.map((item, i) =>
                    item.permission && checkPermission(item.permission) ? (
                      <li className="nav-item me-item" key={i}>
                        <Link
                          to={item.link}
                          className={`nav-link px-0 ${locationHook.pathname === item.link ? 'active' : ''}`}
                          onClick={() => {
                            if (item.link === all_routes.addservice) {
                              setStatusFilter(null);
                              fetchServices(1, null);
                            }
                          }}
                        >
                          <i className={item.icon}></i> {item.label}
                        </Link>
                      </li>
                    ) : null
                  )}
                </ul>
              </div>
            </div>

            <div className="row mt-4" style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}>
              {(['PENDING', 'PENDING_APPROVED', 'APPROVED', 'REJECTED'] as const).map((status) =>
                (status !== 'PENDING_APPROVED' && checkPermission(statusPermissions[status])) ||
                (status === 'PENDING_APPROVED' && isFinanceDepartmentHead && checkPermission(statusPermissions[status]))
                  ? (
                    <div className="col-xl-3 col-sm-6 col-12 mb-4" style={{ padding: '0 15px' }} key={status}>
                      <div
                        className="stat-card bg-white rounded-xl shadow-md overflow-hidden"
                        style={{
                          transition: 'all 0.3s ease',
                          borderRadius: '12px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                          cursor: 'pointer',
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.transform = 'translateY(-5px)';
                          e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                        }}
                        onClick={() => handleCardClick(status)}
                      >
                        <div className="p-6" style={{ padding: '1.5rem' }}>
                          <div
                            className="flex items-center justify-between mb-4"
                            style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                          >
                            <div
                              className={`icon-container ${statusCardStyles[status]?.className || 'bg-gray-100'}`}
                              style={{
                                height: '60px',
                                width: '60px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '12px',
                                backgroundColor: statusCardStyles[status]?.bgColor || '#f3f4f6',
                              }}
                            >
                              <i
                                className={statusCardStyles[status]?.icon || 'fas fa-info-circle'}
                                style={{ fontSize: '1.5rem', color: statusCardStyles[status]?.iconColor || '#6b7280' }}
                              ></i>
                            </div>
                            <span style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937' }}>
                              {status === 'APPROVED'
                                ? counts.approved
                                : status === 'REJECTED'
                                ? counts.rejected
                                : status === 'PENDING'
                                ? counts.pending
                                : counts.pendingApproved}
                            </span>
                          </div>
                          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', margin: '0' }}>
                            {statusLabels[status] || status}
                          </h3>
                        </div>
                      </div>
                    </div>
                  ) : null
              )}
            </div>

            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <div className="card-title">
                  All Service List (Page {pageNo} of {Math.ceil(total / 10) || 1})
                </div>
              </div>
              <div className="card-body">
                {loading && <div className="text-center">Loading...</div>}
                {error && <div className="alert-error">{error}</div>}
                {!loading && !error && services.length === 0 && <div className="alert-info">No services found.</div>}

                {!loading && !error && services.length > 0 && (
                  <div className="table-responsive">
                    <Table className="table text-nowrap">
                      <thead className="table-primary">
                        <tr>
                          <th>ID</th>
                          <th>Vehicle ID</th>
                          <th>Service Type</th>
                          <th>Service KMs</th>
                          <th>Approval Status</th>
                          <th>Service Status</th>
                          <th>Created Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {services.map((service) => (
                          <tr key={service.id}>
                            <td>{service.id}</td>
                            <td>{service.vehicleId}</td>
                            <td>{service.serviceType}</td>
                            <td>{service.serviceKms}</td>
                            <td>
                              <span className={statusColors[service.approvalStatus || ''] || 'badge bg-secondary'}>
                                {service.approvalStatus || 'N/A'}
                              </span>
                            </td>
                            <td>
                              <span className={statusColors[service.serviceStatus || ''] || 'badge bg-secondary'}>
                                {service.serviceStatus || 'N/A'}
                              </span>
                            </td>
                            <td>{moment(service.createdAt).format('YYYY-MM-DD HH:mm')}</td>
                            <td>
                              <div className="hstack gap-2 fs-3">
                                <Button
                                  variant="icon btn-secondary rounded-circle btn-sm"
                                  onClick={() => handleView(service)}
                                  title="View Service"
                                >
                                  <i className="feather-eye" />
                                </Button>
                                {service.serviceStatus === 'PENDING' && (
                                  <Button
                                    variant="icon btn-success rounded-circle btn-sm"
                                    onClick={() => handleComplete(service)}
                                    title="Complete Service"
                                  >
                                    <i className="feather-check" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}

                <div className="d-flex justify-content-between mt-3">
                  <Button
                    variant="outline-primary"
                    disabled={pageNo === 1}
                    onClick={() => handlePageChange(pageNo - 1)}
                  >
                    Previous
                  </Button>
                  <span>
                    Page {pageNo} of {Math.ceil(total / 10) || 1}
                  </span>
                  <Button
                    variant="outline-warning"
                    disabled={pageNo >= Math.ceil(total / 10)}
                    onClick={() => handlePageChange(pageNo + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>

            <ViewServiceModal
              show={showViewModal}
              onHide={() => setShowViewModal(false)}
              serviceId={selectedServiceId}
              statusColors={statusColors}
            />

            <CompleteModule
              show={showCompleteModal}
              onHide={() => setShowCompleteModal(false)}
              serviceId={selectedService?.id || 0}
              onComplete={handleCompleteSuccess}
              initialPartName={initialPartName}
            />

            <ApprovedCompletedModule
              show={showApprovedModal}
              onHide={() => setShowApprovedModal(false)}
              statusColors={statusColors}
            />
            <RejectedCompletedModule
              show={showRejectedModal}
              onHide={() => setShowRejectedModal(false)}
              statusColors={statusColors}
            />
            <PendingCompletedModule
              show={showPendingModal}
              onHide={() => setShowPendingModal(false)}
              statusColors={statusColors}
            />
            <PendingApprovedModule
              show={showPendingApprovedModal}
              onHide={() => setShowPendingApprovedModal(false)}
              statusColors={statusColors}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Service;